import { useRef, useState } from "react"

import useSlugExtractor from "@hooks/useSlugExtractor"

import { faIcon } from "@utils/faIcon"
import replaceSlugs from "@utils/replaceSlugs"

import { BULLET_CHARACTER } from "@particles/Bullet/Bullet"

import { Avatar, Button } from "@atoms/index"

import ListItem from "@molecules/ListItem/ListItem"
import ListItemContent from "@molecules/ListItem/ListItemContent/ListItemContent"

import type { ScannedCode } from "@pages/Pricebook/components/CodeScanner/CodeScanner.types"
import CodeScannerDialog from "@pages/Pricebook/components/CodeScannerDialog/CodeScannerDialog"
import cleanBarcode from "@pages/Pricebook/components/CodeScannerDialog/cleanBarcode"
import useBarcodeLookupQuery from "@pages/Pricebook/components/CodeScannerDialog/useBarcodeLookupQuery"

import { PRICEBOOK_ENDPOINTS } from "@endpoints/pricebook"

import { fetchPriceBookItemDetails } from "./LookupCodeDialog.store"
import { LookupCodeDialogProps } from "./LookupCodeDialog.types"

type SelectState = "idle" | "fetching" | "failed"

const partIcon = faIcon({ name: "box-taped", style: "regular" })
const codeIcon = faIcon({ name: "qrcode", style: "regular" })

// The part's identity line under its description: manufacturer and part number, whichever it has
function partContext(part: PricebookItemSearchResult): string {
    return [part.manufacturer, part.part_number].filter((segment) => segment !== "").join(` ${BULLET_CHARACTER} `)
}

// The scanner in lookup mode: the camera keeps running, the last code read shows the part it belongs to (or why it
// can't be used) under the viewfinder, and Select hands the part's detail payload to the form.
function LookupCodeDialog(props: LookupCodeDialogProps) {
    const { isOpen, onOpenChange, onSelect } = props

    const [scannedCode, setScannedCode] = useState("")
    const [selectState, setSelectState] = useState<SelectState>("idle")
    // A details fetch that outlives its code (a new scan, Cancel) must not fill the form when it lands
    const selectRequestRef = useRef(0)
    const [detailsEndpointTemplate] = useSlugExtractor([PRICEBOOK_ENDPOINTS.DETAILS])

    const lookup = useBarcodeLookupQuery(scannedCode)
    const part = lookup.data ?? null
    const isSelectable = part !== null && part.is_active && part.confirmed

    const resetSelection = () => {
        selectRequestRef.current += 1
        setSelectState("idle")
    }

    const handleOpenChange = (nextIsOpen: boolean) => {
        onOpenChange(nextIsOpen)

        if (!nextIsOpen) {
            setScannedCode("")
            resetSelection()
        }
    }

    const handleCode = (code: ScannedCode) => {
        const cleanedCode = cleanBarcode(code.value)

        if (cleanedCode !== "") {
            setScannedCode(cleanedCode)
            resetSelection()
        }
    }

    const handleSelect = async () => {
        if (part) {
            const request = (selectRequestRef.current += 1)
            setSelectState("fetching")

            try {
                const priceBookItem = await fetchPriceBookItemDetails(
                    replaceSlugs(detailsEndpointTemplate, { id: part.id }),
                )

                if (request === selectRequestRef.current) {
                    onSelect(priceBookItem)
                    handleOpenChange(false)
                }
            } catch {
                if (request === selectRequestRef.current) {
                    setSelectState("failed")
                }
            }
        }
    }

    let card = null

    if (scannedCode !== "" && lookup.isLoading) {
        card = (
            <ListItem isLoading={true}>
                <ListItem.AvatarAndContent
                    avatar={<Avatar variant="object" size="lg" icon={codeIcon} />}
                    content={<ListItemContent leftLabel={<ListItem.Label label={scannedCode} />} />}
                />
            </ListItem>
        )
    } else if (lookup.isError) {
        card = (
            <ListItem>
                <ListItem.AvatarAndContent
                    avatar={<Avatar variant="object" size="lg" icon={codeIcon} />}
                    content={
                        <ListItemContent
                            leftLabel={<ListItem.Label label={scannedCode} truncate={true} />}
                            leftCaption={<ListItem.Caption caption="Couldn't look up this code" state="warning" />}
                        />
                    }
                />
                <ListItem.Actions>
                    <Button
                        type="button"
                        variant="subtle"
                        size="sm"
                        onClick={() => void lookup.refetch()}
                        noDebounce={true}
                    >
                        Retry
                    </Button>
                </ListItem.Actions>
            </ListItem>
        )
    } else if (scannedCode !== "" && part === null) {
        card = (
            <ListItem>
                <ListItem.AvatarAndContent
                    avatar={<Avatar variant="object" size="lg" icon={codeIcon} />}
                    content={
                        <ListItemContent
                            leftLabel={<ListItem.Label label={scannedCode} truncate={true} />}
                            leftCaption={<ListItem.Caption caption="No part has this code" state="warning" />}
                        />
                    }
                />
            </ListItem>
        )
    } else if (part !== null) {
        let caption = null

        if (!part.is_active) {
            caption = <ListItem.Caption caption="This part is inactive" state="warning" />
        } else if (!part.confirmed) {
            caption = <ListItem.Caption caption="This part hasn't been confirmed yet" state="warning" />
        } else if (selectState === "failed") {
            caption = <ListItem.Caption caption="Couldn't load this part. Select it again." state="warning" />
        } else if (partContext(part) !== "") {
            caption = <ListItem.Caption caption={partContext(part)} truncate={1} />
        }

        card = (
            <ListItem>
                <ListItem.AvatarAndContent
                    avatar={<Avatar variant="object" size="lg" icon={partIcon} src={part.image_url || undefined} />}
                    content={
                        <ListItemContent
                            leftLabel={<ListItem.Label label={part.description} truncate={true} />}
                            leftCaption={caption}
                        />
                    }
                />
            </ListItem>
        )
    }

    return (
        <CodeScannerDialog
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            title="Part code lookup"
            onCode={handleCode}
            result={card}
            actions={
                <Button
                    type="button"
                    variant="solid"
                    size="lg"
                    onClick={() => void handleSelect()}
                    isDisabled={!isSelectable}
                    isProcessing={selectState === "fetching"}
                    noDebounce={true}
                >
                    Select
                </Button>
            }
        />
    )
}

export default LookupCodeDialog
